import { useEffect, useState } from 'react';

import {
  failConnectionAddOperation,
  failConnectionReconnectOperation,
  startConnectionAddOperation,
  startConnectionReconnectOperation,
} from '@mint-lib/connections-ui';
import type {
  AuthorizeResult,
  NormalizedConnection,
  OAuthFlow,
  OAuthResumeContext,
  RedirectTarget,
} from '@mint-lib/integration-registry';
import { useQuery } from '@mint-lib/query-client';
import { PlatformUrns } from '@mint-sdk/api-integrations';
import { Integrations, LocalStorage } from '@mint-sdk/api-omni-trafficking';

// The literal constant (not `cmIntegrationMeta.urn`, which is typed as the
// widened `PlatformUrn` template-literal type) so it type-checks against
// `LocalStorage`'s narrower `TraffickingPlatformUrnType` parameter.
const CM_URN = PlatformUrns.TRAFFICKING_PLATFORM_URN.campaignManager;

/**
 * `canProcess` / `resumeRedirect` per the shared `OAuthFlow` contract. Ported
 * from `packages/connections/src/components/_organisms/TraffickingAddConnectionForm/traffickingOAuthCallbackStrategies.ts`'s
 * `cmOauthStrategy.isEnabled` and the CM `beforeLoad` block in
 * `apps/routing/src/routes/_secured/company-profile/integrations/$integration/connections/add.tsx`.
 */
function canProcess(search: Record<string, unknown>): boolean {
  return typeof search.state === 'string' && typeof search.code === 'string';
}

/**
 * Pure decision function for the "add page redirects to update page mid-OAuth"
 * hack: CM's OAuth `redirect_uri` always points back at the add route, even
 * when the flow was started from the manage/update page to reconnect an
 * *existing* connection. If a connection id is already stashed for this
 * `state`, the callback belongs to that reconnect, not a fresh add — so the
 * caller should redirect to the update route instead of letting the add page
 * process it.
 *
 * Reads the exact same `LocalStorage` keys/API as the legacy `beforeLoad`
 * block (`getConnectionIdFrom` / `getOauthFlowIdFrom` for
 * `TRAFFICKING_PLATFORM_URN.campaignManager`), so an in-flight redirect
 * started before this registry existed is never orphaned.
 */
function resumeRedirect(ctx: OAuthResumeContext): RedirectTarget | null {
  const state =
    typeof ctx.search.state === 'string' ? ctx.search.state : undefined;
  const code =
    typeof ctx.search.code === 'string' ? ctx.search.code : undefined;
  if (!state || !code) return null;

  const connectionId = LocalStorage.getConnectionIdFrom(CM_URN);
  const oauthFlowId = LocalStorage.getOauthFlowIdFrom(CM_URN, state);
  if (!connectionId || !oauthFlowId) return null;

  return {
    to: '/company-profile/integrations/$integration/connections/$connection/update',
    params: {
      integration: ctx.params.integration ?? CM_URN,
      connection: connectionId,
    },
  };
}

/**
 * Wider than `OAuthFlow['useAuthorize']` (all extra fields optional, so it
 * still satisfies the shared interface structurally) — `add-form`/`manage-form`
 * import this module directly to pass the extra context CM's authorize step
 * needs that `NormalizedConnection` alone doesn't carry (the in-progress
 * connection name, and the profile/advertiser selection to preserve across
 * the off-site redirect).
 */
export interface CMAuthorizeParams {
  connection?: NormalizedConnection;
  connectionName?: string;
  profileId?: string;
  advertiserIds?: string[];
}

/**
 * Ported from
 * `packages/connections/src/components/_organisms/ManageConnectionForm/traffickingReconnectStrategies.ts`'s
 * `cmAuthorizeStrategy`. Note the faithfully-preserved legacy quirk: the
 * oauth-url query is only ever `enabled` when there's *no* `connectionId` yet
 * — for the manage-page reconnect path (which always has a `connectionId`)
 * this never actually re-fetches a new authorization URL. Kept byte-for-byte
 * rather than "fixed", per the port's own additive-only mandate.
 */
function useAuthorize(props: CMAuthorizeParams): AuthorizeResult {
  const connectionId = props.connection?.id;
  const [isEnabled, setIsEnabled] = useState(false);

  // TODO(cm360BackendOwnedAuth): this package still implements the
  // frontend-owned flow (oauthFlowId in localStorage), i.e. the SDK's
  // `*Legacy` endpoints; port the backend-owned flow before the flag is removed.
  const { data, error, isLoading, refetch } = useQuery({
    ...Integrations.getCMIntegrationOAuthUrlLegacy({
      params: { integrationId: connectionId ?? null },
    }),
    enabled: isEnabled && !connectionId,
  });

  useEffect(() => {
    if (!data?.oauthFlowId || !props.connectionName) return;
    LocalStorage.setCMOauthFlow(
      props.connectionName,
      data.oauthFlowId,
      props.profileId,
      props.advertiserIds,
      connectionId,
    );
  }, [
    data?.oauthFlowId,
    props.connectionName,
    props.profileId,
    props.advertiserIds,
    connectionId,
  ]);

  useEffect(() => {
    if (!data?.authorizationUrl) return;
    window.location.href = data.authorizationUrl;
  }, [data?.authorizationUrl]);

  useEffect(() => {
    if (!error) return;
    failConnectionAddOperation('error');
    failConnectionReconnectOperation('error');
  }, [error]);

  const authorize = (): void => {
    // Without a connection this is the add form; with one, a reconnect.
    const context = {
      platform: CM_URN,
      flow: 'oauth',
      registry: true,
    } as const;
    if (connectionId) {
      startConnectionReconnectOperation(context);
    } else {
      startConnectionAddOperation(context);
    }
    LocalStorage.resetCMOauthFlow();
    setIsEnabled(true);
    void refetch();
  };

  return { authorize, isPending: isLoading };
}

export const cmOAuthFlow: OAuthFlow = {
  canProcess,
  useAuthorize,
  resumeRedirect,
};

// Exported standalone (wider `CMAuthorizeParams` type) so `add-form`/
// `manage-form` can pass `connectionName`/`profileId`/`advertiserIds` directly
// instead of going through the narrower `OAuthFlow` interface.
export { resumeRedirect, useAuthorize };

/** Reads the OAuth-return query params directly from the browser location,
 * since neither `AddFormProps` nor `ManageFormProps` thread router search
 * params through to the mounted form. */
export function getBrowserOAuthParams(): {
  state: string | undefined;
  code: string | undefined;
} {
  if (typeof window === 'undefined') {
    return { state: undefined, code: undefined };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    state: params.get('state') ?? undefined,
    code: params.get('code') ?? undefined,
  };
}

export interface CMOAuthCallbackResult {
  /** e.g. `'PROFILE_UPDATE_REQUIRED'` — drives the manage form's profile-refresh banner. */
  connectStatus: string | undefined;
  isProcessing: boolean;
  error: unknown;
  reset: () => void;
}

/**
 * Processes the CM OAuth callback (`state`+`code` on the current page) once
 * the required local-storage bookkeeping is present. Ported from
 * `cmOauthStrategy.useQuery` in `traffickingOAuthCallbackStrategies.ts`.
 */
export function useCMOAuthCallback(search: {
  state: string | undefined;
  code: string | undefined;
}): CMOAuthCallbackResult {
  const { mutate, data, error, isPending, reset } =
    Integrations.postCMIntegrationOAuthCallback();

  const oauthFlowId = LocalStorage.getOauthFlowIdFrom(CM_URN, search.state);
  const connectionName = LocalStorage.getConnectionNameFrom(
    CM_URN,
    search.state,
  );
  const connectionId = LocalStorage.getConnectionIdFrom(CM_URN);

  useEffect(() => {
    if (!search.state || !search.code || !oauthFlowId || !connectionName) {
      return;
    }
    mutate({
      data: {},
      params: {
        state: search.state,
        code: search.code,
        oauthFlowId,
        integrationId: connectionId ?? null,
      },
    });
    // Mirrors the legacy effect's dependency list exactly — `mutate` is stable
    // per-mount and `reset` is intentionally excluded so this fires once per
    // (state, code) pair.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.state, search.code, oauthFlowId, connectionName, connectionId]);

  return {
    connectStatus: data?.reconnectState,
    isProcessing: isPending,
    error,
    reset,
  };
}
